import { useMemo, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { getCourtLabel, getWeekdayFromDateStr } from '../utils/bookingUtils';
import {
  getActivePackages,
  getPackageRateBands,
  getPackageOptionsForDay,
  getOptionRates,
  getSelectablePackageDurations,
  getPackagePrice,
  getPackageInclusions,
  getPackageCourts,
  describePackageWindow,
  formatPackageHour,
  packageAllowsCustomDuration,
  getPackageOptionChoices,
  formatPackagePriceRange,
  getPackageHeading,
  describeRateDays,
  getOptionRateForDay,
  packageIsHourlyPriced,
  describePackageBandHours,
  describePackageHourlyCharge,
  WEEKDAYS,
} from '../utils/packageUtils';
import { getPackageStartOptions, getPackageEndOptions } from '../utils/packageAvailabilityUtil';
import '../css/PackageBooking.css';
import { formatMoney } from '../utils/currencyUtil';

// The guest-facing package booking flow, in three steps that sit in front of
// the existing details -> checkout -> confirmation modals:
//
//   1. PackagePickerModal       — only when the club offers more than one
//   2. PackageOptionPickerModal — which duration, only when several are sold
//   3. PackageDetailsModal      — the "Exclusive Court Use" card: rates + inclusions
//   4. PackageDateTimeModal     — date and start time, with availability
//
// Duration is chosen BEFORE the details card rather than on the date screen:
// what a package includes and costs depends on which one you picked, so the
// card can only answer "what am I buying?" once that's settled.
//
// All three are presentational: they take config-derived data and call back.
// Availability and pricing live in packageAvailabilityUtil/packageUtils so the
// same answers hold whether they're asked here or at booking time.

/** Entry point on the booking screen. Renders nothing when no package is offered. */
export function PackageEntryButton({ onClick }) {
  const { config } = useConfig();
  const packages = getActivePackages(config);
  if (packages.length === 0) return null;

  // One package: name the thing, since "Book Events/Packages with us" tells a
  // guest less than "Exclusive Court Use" does. Several: the generic invite.
  const label = packages.length === 1
    ? (packages[0].name || '').trim() || 'Book a package'
    : 'Book Events/Packages with us';

  return (
    <button type="button" className="pkg-entry-btn" onClick={() => onClick(packages.length === 1 ? packages[0] : null)}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </svg>
      {label}
    </button>
  );
}

/** Step 1 — choose between several packages. */
export function PackagePickerModal({ onSelect, onClose }) {
  const { config } = useConfig();
  const packages = getActivePackages(config);

  return (
    <div className="user-info-form pkg-modal">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">Events &amp; Packages</h2>
          <button type="button" className="uif-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <p className="pkg-lede">Pick the package you&apos;d like to book. We&apos;ll check court availability for your date next.</p>

        <div className="pkg-list">
          {packages.map((pkg) => {
            const durations = getSelectablePackageDurations(pkg);
            return (
              <button type="button" key={pkg.id} className="pkg-list-item" onClick={() => onSelect(pkg)}>
                <span className="pkg-list-name">{(pkg.name || '').trim() || 'Package'}</span>
                {pkg.description && <span className="pkg-list-desc">{pkg.description}</span>}
                <span className="pkg-list-meta">
                  {describePackageWindow(pkg)}
                  {durations.length > 0 && ` · ${durations.length === 1 ? `${durations[0]} hrs` : `${durations[0]}–${durations[durations.length - 1]} hrs`}`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** A right-pointing chevron, matching the affordance on the picker rows. */
function Chevron() {
  return (
    <svg className="pkg-option-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/**
 * Step 2 — which duration. Shown only when the package sells more than one;
 * a list with a single row asks nothing.
 *
 * Selection is held by the caller, not here, so backing out of the details
 * card returns to this list with the guest's choice still highlighted.
 */
export function PackageOptionPickerModal({ pkg, selectedOptionId, onSelectOption, onProceed, onBack, onClose }) {
  // Needed for the currency symbol on each option's price range below;
  // the other modals in this file already read it the same way.
  const { config } = useConfig();
  const choices = getPackageOptionChoices(pkg, null);

  return (
    <div className="user-info-form pkg-modal">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">{getPackageHeading(pkg)}</h2>
          <button type="button" className="uif-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <p className="pkg-lede">Tap a package to see what&apos;s included.</p>

        <div className="pkg-list">
          {choices.map((choice) => {
            const selected = String(choice.id) === String(selectedOptionId);
            return (
              <button
                type="button"
                key={choice.id}
                className={`pkg-option-item${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onSelectOption(choice.id)}
              >
                <span className="pkg-option-name">{choice.label}</span>
                <span className="pkg-option-price">{formatPackagePriceRange(choice.minPrice, choice.maxPrice, config)}</span>
                <Chevron />
              </button>
            );
          })}
        </div>

        <div className="pkg-actions">
          {onBack && <button type="button" className="pkg-btn-ghost" onClick={onBack}>Back</button>}
          <button type="button" className="pkg-btn" disabled={!selectedOptionId} onClick={onProceed}>
            Proceed on this package
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 3 — the package's own page: what it is, what it costs, what's included.
 *
 * `option` is the duration chosen on the step before, when there was one. It
 * narrows the RATES section to that duration's prices — having just picked
 * "4 hours", a guest re-reading the 2- and 5-hour prices here would only
 * wonder which one they are about to be charged.
 */
export function PackageDetailsModal({ pkg, option = null, onProceed, onBack, onClose }) {
  const { config } = useConfig();
  // Hourly bands price durations the presets don't cover, so they're beside
  // the point once a preset has been chosen.
  const bands = option ? [] : getPackageRateBands(pkg);
  const inclusions = getPackageInclusions(pkg);
  const courts = getPackageCourts(pkg);

  // Rates are grouped by the days they apply to — "Monday – Friday" once with
  // its bands under it, rather than repeating the day list on every line, the
  // way the reference screen reads.
  const rateGroups = useMemo(() => {
    const groups = new Map();
    bands.forEach((band) => {
      const key = describeRateDays(band);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(band);
    });
    return [...groups.entries()].map(([label, list]) => ({ label, bands: list }));
  }, [bands]);

  // Fixed durations, grouped the same way as the hourly bands: one heading
  // per day range with its prices under it. A 4-hour option priced twice
  // (weekday/weekend) therefore appears once under each heading rather than
  // repeating the day list on every line.
  const fixedGroups = useMemo(() => {
    const groups = new Map();
    (option ? [option] : getPackageOptionsForDay(pkg, null)).forEach((opt) => {
      getOptionRates(opt).forEach((rate) => {
        const key = describeRateDays(rate);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ id: `${opt.id}-${rate.id}`, hours: opt.hours, price: rate.price });
      });
    });
    return [...groups.entries()].map(([label, rows]) => ({ label, rows }));
  }, [pkg, option]);

  return (
    <div className="user-info-form pkg-modal">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">{(pkg.name || '').trim() || 'Package'}</h2>
          <button type="button" className="uif-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Names the choice made on the step before, so the guest can see at
            a glance which package these rates and inclusions belong to. */}
        {option && (
          <p className="pkg-chosen">{`${Number(option.hours) || 0}-Hour Package`}</p>
        )}

        {/* .admin-text keeps the line breaks the club typed in Config. */}
        {pkg.description && <p className="pkg-lede admin-text">{pkg.description}</p>}

        {(rateGroups.length > 0 || fixedGroups.length > 0) && (
          <>
            <h3 className="pkg-section-title">Rates</h3>
            {fixedGroups.map(group => (
              <div className="pkg-rate-group" key={`fixed-${group.label}`}>
                <div className="pkg-rate-days">{group.label}</div>
                {group.rows.map(row => (
                  <div className="pkg-tick-row" key={row.id}>
                    <span className="pkg-tick" aria-hidden="true">✓</span>
                    <span>{row.hours} hours: {formatMoney(Number(row.price || 0), config)}</span>
                  </div>
                ))}
              </div>
            ))}
            {/* More than one price means the date decides which — say so here
                rather than letting the number change unannounced on the next
                screen. */}
            {fixedGroups.length > 1 && (
              <p className="pkg-rate-note">Which rate applies depends on the day you pick next.</p>
            )}
            {rateGroups.map(group => (
              <div className="pkg-rate-group" key={group.label}>
                <div className="pkg-rate-days">{group.label}</div>
                {group.bands.map(band => (
                  <div className="pkg-tick-row" key={band.id}>
                    <span className="pkg-tick" aria-hidden="true">✓</span>
                    <span>
                      {describePackageBandHours(band)}: {formatMoney(Number(band.price || 0), config)}/hr
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}

        {inclusions.length > 0 && (
          <>
            <h3 className="pkg-section-title">Inclusions</h3>
            {inclusions.map((text, i) => (
              <div className="pkg-tick-row pkg-tick-row-lined" key={i}>
                <span className="pkg-tick" aria-hidden="true">✓</span>
                <span className="admin-text">{text}</span>
              </div>
            ))}
          </>
        )}

        {courts.length > 0 && (
          <p className="pkg-courts-note">
            Covers {courts.map(c => getCourtLabel(config, c)).join(', ')} · available {describePackageWindow(pkg)}
          </p>
        )}

        <div className="pkg-actions">
          {onBack && <button type="button" className="pkg-btn-ghost" onClick={onBack}>Back</button>}
          <button type="button" className="pkg-btn" onClick={onProceed}>Proceed</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 3 — when the event is.
 *
 * Duration drives everything: with fixed options the guest picks one and the
 * end time is derived, and with hourly pricing they pick any length that fits
 * the window. Start times are listed with the unavailable ones disabled and
 * explained, rather than quietly omitted — "why can't I pick 5PM?" is the
 * question a shorter list leaves unanswered.
 */
export function PackageDateTimeModal({
  pkg, minDate, dateStr, hours, startHour, bookedSlots, currentHour, lockHours = false,
  onDateChange, onHoursChange, onStartChange, onEndChange, onContinue, onBack, onClose, loadingSlots,
}) {
  const { config } = useConfig();
  const [touched, setTouched] = useState(false);

  const weekday = dateStr ? getWeekdayFromDateStr(dateStr) : null;
  const durations = getSelectablePackageDurations(pkg, weekday);

  // The chosen duration re-priced for the chosen date. A package sold at
  // ₱1,500 on weekdays and ₱2,000 at the weekend must show the guest which
  // one they are on before they commit, and must say plainly when the date
  // they picked isn't covered at all rather than failing as "we can't price
  // that".
  const chosenOption = hours
    ? getPackageOptionsForDay(pkg, null).find(opt => Number(opt.hours) === Number(hours))
    : null;
  const dayRate = (chosenOption && weekday !== null) ? getOptionRateForDay(chosenOption, weekday) : null;
  const optionUnavailable = !!chosenOption && weekday !== null && dayRate === null;

  // An hourly-priced package sells any run of hours inside its window, so the
  // guest picks a start AND an end instead of a preset duration — the length
  // is whatever falls between them.
  const hourly = packageIsHourlyPriced(pkg);
  const endHour = (hourly && startHour !== null && startHour !== undefined && hours)
    ? Number(startHour) + Number(hours)
    : null;
  // Hourly: a start counts as offerable if even one hour is free there; how
  // far it can run is then answered by the end list. Preset: the duration is
  // already fixed, so a start is offerable only if the whole run fits.
  const startOptions = useMemo(() => {
    if (!dateStr) return [];
    const probe = hourly ? 1 : hours;
    return probe ? getPackageStartOptions({ config, pkg, hours: probe, bookedSlots, currentHour }) : [];
  }, [config, pkg, hourly, hours, bookedSlots, currentHour, dateStr]);

  const endOptions = useMemo(
    () => ((hourly && dateStr && startHour !== null && startHour !== undefined)
      ? getPackageEndOptions({ config, pkg, startHour, bookedSlots, currentHour })
      : []),
    [config, pkg, hourly, startHour, bookedSlots, currentHour, dateStr],
  );

  const chosen = startOptions.find(o => o.hour === startHour);
  const chosenEnd = endOptions.find(o => o.hour === endHour);
  const price = (dateStr && hours && startHour !== null && startHour !== undefined)
    ? getPackagePrice(pkg, startHour, hours, weekday)
    : null;
  const hourlyCharge = (hourly && price !== null)
    ? describePackageHourlyCharge(pkg, startHour, hours, weekday, config)
    : '';

  // Hourly needs BOTH ends to hold: the start must be free and the run to the
  // chosen end must be too.
  const canContinue = !!dateStr && !!hours && chosen?.available && price !== null
    && (!hourly || !!chosenEnd?.available);
  const anyAvailable = startOptions.some(o => o.available);

  return (
    <div className="user-info-form pkg-modal">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">Your event date and time</h2>
          <button type="button" className="uif-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <p className="pkg-lede">
          Let us know when your event is. Choose a date, then a start time, and we&apos;ll check court
          availability for you.
        </p>

        <div className="form-group">
          <label htmlFor="pkg-date">Event date</label>
          <input
            id="pkg-date"
            type="date"
            min={minDate}
            value={dateStr || ''}
            onChange={(e) => { setTouched(true); onDateChange(e.target.value); }}
          />
        </div>

        {/* With one fixed duration there's nothing to choose — state it rather
            than rendering a select with a single option. Same when the guest
            already picked one on the option step: re-asking here would let
            them end up with a duration they never saw the price of. Back
            returns to that step to change it. */}
        {hourly ? (
          /* Nothing to choose here — the start and end selects below set the
             length between them. */
          <p className="pkg-fixed-duration">
            Pick any start and end time inside {describePackageWindow(pkg)}. You&apos;re charged by the hour.
          </p>
        ) : lockHours && hours ? (
          <p className="pkg-fixed-duration">
            You picked the <strong>{hours}-Hour Package</strong>. Use Back to change it.
          </p>
        ) : durations.length > 1 ? (
          <div className="form-group">
            <label htmlFor="pkg-hours">How long?</label>
            <select id="pkg-hours" value={hours || ''} onChange={(e) => onHoursChange(Number(e.target.value) || null)}>
              <option value="">—</option>
              {durations.map(h => <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>)}
            </select>
          </div>
        ) : durations.length === 1 ? (
          <p className="pkg-fixed-duration">This package runs for <strong>{durations[0]} hours</strong>.</p>
        ) : (
          <p className="pkg-error">This package has no rates set up yet. Please contact us and we&apos;ll book it for you.</p>
        )}

        {/* What an hourly booking works out to: the rate (or rates, when the
            run straddles a change) and the resulting total. */}
        {hourly && hourlyCharge && (
          <p className="pkg-rate-applied">
            <span className="pkg-rate-applied-days">{WEEKDAYS[weekday]?.label}</span>
            <span>
              {hourlyCharge} · {hours} hour{hours === 1 ? '' : 's'} · <strong>{formatMoney(Number(price), config)}</strong>
            </span>
          </p>
        )}

        {/* Which of the package's rates the chosen date lands on. Labelled
            with the weekday the guest actually picked, not the rate's whole
            scope: on a Tuesday booking, "Monday – Friday" is the club's
            pricing rule, while "Tuesday" is the answer to "what am I paying
            for THIS date?". */}
        {dayRate && (
          <p className="pkg-rate-applied">
            <span className="pkg-rate-applied-days">{WEEKDAYS[weekday]?.label}</span>
            <span>
              {hours} hour{hours === 1 ? '' : 's'} · <strong>{formatMoney(Number(dayRate.price || 0), config)}</strong>
            </span>
          </p>
        )}

        {optionUnavailable && (
          <p className="pkg-error">
            The {hours}-Hour Package isn&apos;t offered on that day. Pick another date, or go back and
            choose a different package.
          </p>
        )}

        <div className="pkg-time-row">
          <div className="form-group">
            <label htmlFor="pkg-start">Start time</label>
            <select
              id="pkg-start"
              value={startHour ?? ''}
              // Hourly has no duration to wait for — the start is the first
              // thing the guest chooses after the date.
              disabled={!dateStr || (!hourly && !hours) || loadingSlots}
              onChange={(e) => { setTouched(true); onStartChange(e.target.value === '' ? null : Number(e.target.value)); }}
            >
              <option value="">—</option>
              {startOptions.map(opt => (
                <option key={opt.hour} value={opt.hour} disabled={!opt.available}>
                  {opt.label}{opt.available ? '' : ' — unavailable'}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="pkg-end">End time</label>
            {hourly ? (
              /* Chosen, not derived: with hourly pricing the end IS the
                 length. Ends that would run into another booking are listed
                 but disabled, so "why can't I stay till 10?" is answered. */
              <select
                id="pkg-end"
                value={endHour ?? ''}
                disabled={!dateStr || startHour === null || startHour === undefined || loadingSlots}
                onChange={(e) => { setTouched(true); onEndChange(e.target.value === '' ? null : Number(e.target.value)); }}
              >
                <option value="">—</option>
                {endOptions.map(opt => (
                  <option key={opt.hour} value={opt.hour} disabled={!opt.available}>
                    {opt.label}{opt.available ? '' : ' — unavailable'}
                  </option>
                ))}
              </select>
            ) : (
              /* Derived, never entered: the duration already fixed it, and an
                 editable end box invites a mismatch with the package length. */
              <input
                type="text"
                readOnly
                value={startHour !== null && startHour !== undefined && hours ? formatPackageHour(startHour + hours) : '—'}
                aria-label="End time (calculated)"
              />
            )}
          </div>
        </div>

        {!loadingSlots && chosenEnd && !chosenEnd.available && (
          <p className="pkg-error">{chosenEnd.reason}</p>
        )}

        {loadingSlots && <p className="pkg-hint">Checking court availability…</p>}

        {!loadingSlots && dateStr && hours && !anyAvailable && (
          <p className="pkg-error">
            Sorry — this package is fully booked on that date. Please try another day.
          </p>
        )}

        {!loadingSlots && chosen && !chosen.available && (
          <p className="pkg-error">{chosen.reason}</p>
        )}

        {!loadingSlots && touched && dateStr && hours && chosen?.available && price === null && (
          <p className="pkg-error">
            We can&apos;t price that time online yet. Please contact us and we&apos;ll sort it out for you.
          </p>
        )}

        {canContinue && (
          <div className="pkg-quote">
            <span>
              {formatPackageHour(startHour)} – {formatPackageHour(startHour + hours)} · {hours} hour{hours === 1 ? '' : 's'}
            </span>
            <strong>{formatMoney(price, config)}</strong>
          </div>
        )}

        <div className="pkg-actions">
          {onBack && <button type="button" className="pkg-btn-ghost" onClick={onBack}>Back</button>}
          <button type="button" className="pkg-btn" disabled={!canContinue} onClick={onContinue}>Continue</button>
        </div>
      </div>
    </div>
  );
}
